import { useState, useMemo } from "react";
import { UserName } from "@/components/badges/UserName";
import { useAuth } from "@/lib/auth";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { UserAvatar } from "@/components/ui/UserAvatar";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Loader2, 
  Users, 
  GraduationCap, 
  Calendar, 
  MessageSquare, 
  Video, 
  UserCircle,
  Search,
  ExternalLink,
  X
} from "lucide-react";
import { toast } from "sonner";
import { format, isFuture } from "date-fns";
import { useNavigate } from "react-router-dom";
import { useGetOrCreateThread } from "@/hooks/useMessageThreads";

interface ConnectionProfile {
  id: string;
  full_name: string;
  role: string;
  avatar_url: string | null;
  bio: string | null;
}

interface UnifiedConnection {
  id: string;
  requester_id: string;
  recipient_id: string;
  status: string;
  connection_type: "community" | "mentorship";
  message: string | null;
  scheduled_at: string | null;
  zoom_link: string | null;
  thread_id: string | null;
  is_mentorship_active: boolean;
  mentorship_completion_note: string | null;
  mentorship_completed_at: string | null;
  created_at: string;
  accepted_at: string | null;
  requester?: ConnectionProfile;
  recipient?: ConnectionProfile;
}

type ConnectionFilter = "all" | "community" | "mentorship";
type SortOption = "recent" | "name" | "type";

export function UnifiedConnectionsSection() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const getOrCreateThread = useGetOrCreateThread();
  
  const [filter, setFilter] = useState<ConnectionFilter>("all");
  const [sortBy, setSortBy] = useState<SortOption>("recent");
  const [searchQuery, setSearchQuery] = useState("");

  // Fetch all accepted connections from unified table
  const { data: connections, isLoading } = useQuery({
    queryKey: ["unified_connections", user?.id],
    queryFn: async () => {
      if (!user?.id) return [];

      const { data: connectionsData, error } = await supabase
        .from("connections")
        .select("*")
        .or(`requester_id.eq.${user.id},recipient_id.eq.${user.id}`)
        .eq("status", "accepted")
        .order("accepted_at", { ascending: false });

      if (error) throw error;
      if (!connectionsData || connectionsData.length === 0) return [];

      // Fetch profile data for all participants
      const allUserIds = [...new Set(
        connectionsData.flatMap(c => [c.requester_id, c.recipient_id])
      )];

      const { data: profiles } = await supabase
        .from("profiles")
        .select("id, full_name, role, avatar_url, bio")
        .in("id", allUserIds);

      const profileMap = new Map(profiles?.map(p => [p.id, p]) || []);

      return connectionsData.map(c => ({
        ...c,
        requester: profileMap.get(c.requester_id) || null,
        recipient: profileMap.get(c.recipient_id) || null,
      })) as UnifiedConnection[];
    },
    enabled: !!user?.id,
  });

  // Remove connection mutation
  const removeConnection = useMutation({
    mutationFn: async (connectionId: string) => {
      const { error } = await supabase
        .from("connections")
        .delete()
        .eq("id", connectionId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["unified_connections"] });
      toast.success("Connection removed");
    },
    onError: (error: Error) => {
      toast.error("Failed to remove connection: " + error.message);
    },
  });

  // Filtered and sorted connections
  const filteredConnections = useMemo(() => {
    if (!connections) return [];

    let result = connections;

    // Filter by type
    if (filter !== "all") {
      result = result.filter(c => c.connection_type === filter);
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(c => {
        const otherPerson = c.requester_id === user?.id ? c.recipient : c.requester;
        return otherPerson?.full_name?.toLowerCase().includes(query) ||
               otherPerson?.bio?.toLowerCase().includes(query);
      });
    }

    // Sort
    result = [...result].sort((a, b) => {
      if (sortBy === "recent") {
        return new Date(b.accepted_at || b.created_at).getTime() - 
               new Date(a.accepted_at || a.created_at).getTime();
      }
      if (sortBy === "name") {
        const nameA = (a.requester_id === user?.id ? a.recipient : a.requester)?.full_name || "";
        const nameB = (b.requester_id === user?.id ? b.recipient : b.requester)?.full_name || "";
        return nameA.localeCompare(nameB);
      }
      if (sortBy === "type") {
        return a.connection_type.localeCompare(b.connection_type);
      }
      return 0;
    });

    return result;
  }, [connections, filter, searchQuery, sortBy, user?.id]);

  // Counts
  const counts = useMemo(() => ({
    all: connections?.length || 0,
    community: connections?.filter(c => c.connection_type === "community").length || 0,
    mentorship: connections?.filter(c => c.connection_type === "mentorship").length || 0,
  }), [connections]);

  const handleMessage = async (connection: UnifiedConnection) => {
    const otherPerson = connection.requester_id === user?.id ? connection.recipient : connection.requester;
    if (!otherPerson) return;
    
    try {
      const threadId = await getOrCreateThread.mutateAsync(otherPerson.id);
      if (threadId) {
        navigate(`/connections?threadId=${threadId}`);
      }
    } catch (error) {
      toast.error("Failed to open messaging");
    }
  };

  const handleSchedule = async (connection: UnifiedConnection) => {
    const otherPerson = connection.requester_id === user?.id ? connection.recipient : connection.requester;
    if (!otherPerson) return;
    
    try {
      const threadId = await getOrCreateThread.mutateAsync(otherPerson.id);
      if (threadId) {
        navigate(`/connections?threadId=${threadId}&action=schedule`);
      }
    } catch (error) {
      toast.error("Failed to open scheduling");
    }
  };

  const handleViewProfile = (profileId: string) => {
    navigate(`/directory?profile=${profileId}`);
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Filter Tabs */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <Tabs value={filter} onValueChange={(v) => setFilter(v as ConnectionFilter)}>
          <TabsList>
            <TabsTrigger value="all" className="gap-2">
              All Connections ({counts.all})
            </TabsTrigger>
            <TabsTrigger value="community" className="gap-2">
              <Users className="h-4 w-4" />
              Community ({counts.community})
            </TabsTrigger>
            <TabsTrigger value="mentorship" className="gap-2">
              <GraduationCap className="h-4 w-4" />
              Mentorships ({counts.mentorship})
            </TabsTrigger>
          </TabsList>
        </Tabs>

        <div className="flex gap-2 w-full sm:w-auto">
          <div className="relative flex-1 sm:w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search connections..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>
          <Select value={sortBy} onValueChange={(v) => setSortBy(v as SortOption)}>
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="recent">Recent</SelectItem>
              <SelectItem value="name">Name A-Z</SelectItem>
              <SelectItem value="type">Type</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Connection Cards */}
      {filteredConnections.length === 0 ? (
        <EmptyState filter={filter} />
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {filteredConnections.map((connection) => (
            <ConnectionCard
              key={connection.id}
              connection={connection}
              currentUserId={user?.id || ""}
              onMessage={() => handleMessage(connection)}
              onSchedule={() => handleSchedule(connection)}
              onViewProfile={(id) => handleViewProfile(id)}
              onRemove={() => removeConnection.mutate(connection.id)}
              isRemoving={removeConnection.isPending}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function ConnectionCard({
  connection,
  currentUserId,
  onMessage,
  onSchedule,
  onViewProfile,
  onRemove,
  isRemoving,
}: {
  connection: UnifiedConnection;
  currentUserId: string;
  onMessage: () => void;
  onSchedule: () => void;
  onViewProfile: (profileId: string) => void;
  onRemove: () => void;
  isRemoving: boolean;
}) {
  const otherPerson = connection.requester_id === currentUserId 
    ? connection.recipient 
    : connection.requester;
  
  if (!otherPerson) return null;

  const isMentorship = connection.connection_type === "mentorship";
  const isActiveMentorship = isMentorship && connection.is_mentorship_active;
  const hasFutureMeeting = connection.scheduled_at && isFuture(new Date(connection.scheduled_at));
  const isCompleted = !!connection.mentorship_completed_at;

  // Determine relationship label - ONLY for active mentorships
  const getRelationshipLabel = () => {
    // Only show mentor/mentee labels for mentorship connections
    if (!isMentorship) return null;
    
    const otherRole = otherPerson.role;
    
    // If mentorship is completed, show "Former" labels
    if (isCompleted) {
      if (otherRole === "mentor") return "Former Mentor";
      if (otherRole === "entrepreneur") return "Former Mentee";
      return null;
    }
    
    // Only show "Your Mentor/Mentee" for active mentorships
    if (isActiveMentorship) {
      if (otherRole === "mentor") return "Your Mentor";
      if (otherRole === "entrepreneur") return "Your Mentee";
    }
    
    return null;
  };

  return (
    <Card className={`overflow-hidden transition-all ${
      isMentorship 
        ? "border-amber-500/30 bg-gradient-to-br from-amber-50/50 to-background dark:from-amber-950/20" 
        : ""
    }`}>
      <CardContent className="p-4 space-y-4">
        {/* Header with avatar and badges */}
        <div className="flex items-start gap-3">
          <UserAvatar
            avatarPath={otherPerson.avatar_url}
            fullName={otherPerson.full_name || "?"}
            className="h-12 w-12 ring-2 ring-background"
          />
          
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold truncate"><UserName userId={otherPerson.id} name={otherPerson.full_name} /></h3>
            <div className="flex flex-wrap gap-1.5 mt-1">
              {/* Role badge */}
              <Badge variant="secondary" className="text-xs capitalize">
                {otherPerson.role}
              </Badge>
              
              {/* Connection type badge */}
              {isMentorship ? (
                <Badge className="bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-200 text-xs gap-1">
                  <GraduationCap className="h-3 w-3" />
                  Mentorship
                </Badge>
              ) : (
                <Badge variant="outline" className="text-xs gap-1 border-primary/30 text-primary">
                  <Users className="h-3 w-3" />
                  Community
                </Badge>
              )}

              {/* Active mentorship badge */}
              {isActiveMentorship && (
                <Badge className="bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-200 text-xs">
                  Active
                </Badge>
              )}

              {/* Completed badge */}
              {isCompleted && (
                <Badge variant="outline" className="text-xs text-muted-foreground">
                  Completed
                </Badge>
              )}
            </div>
          </div>
        </div>

        {/* Relationship label for mentorship */}
        {getRelationshipLabel() && (
          <p className="text-sm font-medium text-amber-700 dark:text-amber-300">
            {getRelationshipLabel()}
          </p>
        )}

        {/* Bio excerpt */}
        {otherPerson.bio && (
          <p className="text-sm text-muted-foreground line-clamp-2">
            {otherPerson.bio}
          </p>
        )}

        {/* Connected since */}
        <p className="text-xs text-muted-foreground">
          Connected {connection.accepted_at 
            ? format(new Date(connection.accepted_at), "MMM d, yyyy")
            : format(new Date(connection.created_at), "MMM d, yyyy")
          }
        </p>

        {/* Mentorship-specific info */}
        {isMentorship && (
          <div className="space-y-2">
            {/* Next meeting */}
            {hasFutureMeeting && connection.scheduled_at && (
              <div className="flex items-center gap-2 text-sm text-green-700 dark:text-green-300 bg-green-50 dark:bg-green-900/30 p-2 rounded">
                <Calendar className="h-4 w-4" />
                <span>Next Meeting: {format(new Date(connection.scheduled_at), "MMM d 'at' h:mm a")}</span>
              </div>
            )}

            {/* Zoom link */}
            {connection.zoom_link && hasFutureMeeting && (
              <Button
                variant="outline"
                size="sm"
                className="w-full gap-2 border-blue-500/30 text-blue-700 hover:bg-blue-50 dark:text-blue-300"
                onClick={() => window.open(connection.zoom_link!, "_blank")}
              >
                <Video className="h-4 w-4" />
                Join Zoom Meeting
                <ExternalLink className="h-3 w-3" />
              </Button>
            )}

            {/* Completion note */}
            {isCompleted && connection.mentorship_completion_note && (
              <div className="bg-muted/50 p-2 rounded text-sm">
                <p className="text-xs font-medium text-muted-foreground mb-1">Completion Note:</p>
                <p className="text-muted-foreground italic">"{connection.mentorship_completion_note}"</p>
              </div>
            )}
          </div>
        )}

        {/* Actions */}
        <div className="flex flex-wrap gap-2 pt-2 border-t">
          <Button
            variant="ghost"
            size="sm"
            className="gap-1.5"
            onClick={() => onViewProfile(otherPerson.id)}
          >
            <UserCircle className="h-4 w-4" />
            Profile
          </Button>
          
          <Button
            variant="ghost"
            size="sm"
            className="gap-1.5"
            onClick={onMessage}
          >
            <MessageSquare className="h-4 w-4" />
            Message
          </Button>

          {isMentorship && isActiveMentorship && (
            <Button
              variant="ghost"
              size="sm"
              className="gap-1.5"
              onClick={onSchedule}
            >
              <Calendar className="h-4 w-4" />
              Schedule
            </Button>
          )}

          <Button
            variant="ghost"
            size="sm"
            className="gap-1.5 text-destructive hover:text-destructive hover:bg-destructive/10 ml-auto"
            onClick={onRemove}
            disabled={isRemoving}
          >
            {isRemoving ? <Loader2 className="h-4 w-4 animate-spin" /> : <X className="h-4 w-4" />}
            Remove
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function EmptyState({ filter }: { filter: ConnectionFilter }) {
  const navigate = useNavigate();
  
  const messages = {
    all: {
      title: "No connections yet",
      description: "Browse the Directory to connect with mentors and peers.",
      action: "Browse Directory",
    },
    community: {
      title: "No community connections",
      description: "Connect with peers in the Directory to grow your network.",
      action: "Find Peers",
    },
    mentorship: {
      title: "No mentorships",
      description: "Request a mentor from the Directory to get personalized guidance.",
      action: "Find a Mentor",
    },
  };

  const { title, description, action } = messages[filter];

  return (
    <Card className="p-8 text-center">
      <div className="mx-auto max-w-sm space-y-4">
        {filter === "mentorship" ? (
          <GraduationCap className="h-12 w-12 mx-auto text-muted-foreground" />
        ) : (
          <Users className="h-12 w-12 mx-auto text-muted-foreground" />
        )}
        <h3 className="text-lg font-semibold">{title}</h3>
        <p className="text-muted-foreground">{description}</p>
        <Button onClick={() => navigate("/directory")}>
          {action}
        </Button>
      </div>
    </Card>
  );
}

export default UnifiedConnectionsSection;
